import { holds } from './ledger/state';
import type { LedgerView } from './ledger/types';
import type {
  Card,
  ChainState,
  Epitaph,
  Inscription,
  MatchConfig,
  MatchEvent,
  MatchResult,
  MatchState,
  Pawn,
  PendingArrivals,
  PendingEpitaphChoice,
  PendingInscription,
  Phase,
  PileEntry,
  PlayerState,
  ReservePawn,
  RoundSummary,
  Side,
} from './types';

/** A player's public state, plus its own hand when it is the viewer (§14). */
export interface PlayerView extends Omit<PlayerState, 'deck' | 'hand'> {
  readonly deckCount: number;
  readonly handCount: number;
  /** Card ids in hand for the viewer's own side; null for the opponent. */
  readonly hand: readonly string[] | null;
}

/** The redacted state a side may see (§14, §16.1). Contains no deck order and no PRNG state. */
export interface MatchView {
  readonly viewer: Side;
  readonly rulesVersion: string;
  readonly config: MatchConfig;
  readonly seats: Readonly<Record<Side, string>>;
  readonly seq: number;
  /** Only cards whose identity the viewer may know: own hand, pile, Spent and Removed zones. */
  readonly cards: Readonly<Record<string, Card>>;
  readonly pawns: Readonly<Record<string, Pawn>>;
  readonly reservePawns: Readonly<Record<string, ReservePawn>>;
  readonly players: Readonly<Record<Side, PlayerView>>;
  readonly pile: readonly PileEntry[];
  readonly round: number;
  readonly roundStarter: Side;
  readonly turn: number;
  readonly active: Side;
  readonly decider: Side;
  readonly phase: Phase;
  readonly chain: ChainState | null;
  readonly pendingInscription: PendingInscription | null;
  readonly pendingArrivals: PendingArrivals | null;
  readonly pendingEpitaphChoice: PendingEpitaphChoice | null;
  readonly rounds: readonly RoundSummary[];
  readonly result: MatchResult | null;
  /** Long Night tables: the ledger, without its luck stream and with Shrouded Omens unnamed. */
  readonly ledger?: LedgerView;
}

/** The ledger a side may see (§14): no luck stream; a Shrouded Omen is only a dark square. */
function ledgerView(s: MatchState): LedgerView | undefined {
  const l = s.ledger;
  if (!l) return undefined;
  const { luck: _luck, omens, ...rest } = l;
  return {
    ...rest,
    omens: omens.map((o) => (o.shrouded ? { ...o, kind: 'unknown' as const } : o)),
  };
}

/** The match config a side may see: Shrouded Omens are left out (they show in the ledger). */
function configView(config: MatchConfig): MatchConfig {
  const l = config.ledger;
  if (!l || !l.omens.some((o) => o.shrouded)) return config;
  return { ...config, ledger: { ...l, omens: l.omens.filter((o) => !o.shrouded) } };
}

function playerView(p: PlayerState, own: boolean): PlayerView {
  const { deck, hand, ...rest } = p;
  return {
    ...rest,
    spent: rest.spent.slice(),
    removed: rest.removed.slice(),
    epitaphs: rest.epitaphs.slice(),
    pendingCrownBonuses: rest.pendingCrownBonuses.slice(),
    reserves: rest.reserves.slice(),
    reservesPawnedThisRound: rest.reservesPawnedThisRound.slice(),
    collateralUsed: rest.collateralUsed.slice(),
    deckCount: deck.length,
    handCount: hand.length,
    hand: own ? hand.slice() : null,
  };
}

/** view(state, side) → redacted state (§14, §16.1). */
export function view(s: MatchState, side: Side): MatchView {
  const visible: Record<string, Card> = {};
  const reveal = (id: string) => {
    const c = s.cards[id];
    if (c) visible[id] = c;
  };
  const other: Side = side === 'S' ? 'N' : 'S';
  // Long Night: the Broker's Monocle shows the opponent's hand.
  const monocle = !!s.ledger && holds(s, side, 'brokers-monocle');
  s.players[side].hand.forEach(reveal);
  if (monocle) s.players[other].hand.forEach(reveal);
  // Long Night (Veiled): the Widow's inscriptions are hidden from South, on her pawns, in her
  // Epitaphs, and in any choice she has still to make about them.
  const veiled = s.ledger ? veiledFrom(s.config, side) : null;
  let pawns = s.pawns;
  if (veiled) {
    const out: Record<string, MatchState['pawns'][string]> = {};
    for (const [id, p] of Object.entries(s.pawns))
      out[id] = p.side === veiled && p.inscription ? { ...p, inscription: null } : p;
    pawns = out;
  }
  const veil = <T extends { epitaphs: readonly Epitaph[] }>(p: T, owner: Side): T =>
    veiled === owner ? { ...p, epitaphs: p.epitaphs.map(hiddenEpitaph) } : p;
  const ledger = ledgerView(s);
  for (const p of [s.players.S, s.players.N]) {
    p.spent.forEach(reveal);
    p.removed.forEach(reveal);
  }
  for (const e of s.pile) if (e.source === 'card') reveal(e.cardId);
  return {
    viewer: side,
    rulesVersion: s.rulesVersion,
    config: configView(s.config),
    seats: s.seats,
    seq: s.seq,
    cards: visible,
    pawns,
    reservePawns: s.reservePawns,
    players: {
      S: veil(playerView(s.players.S, side === 'S' || (monocle && side === 'N')), 'S'),
      N: veil(playerView(s.players.N, side === 'N' || (monocle && side === 'S')), 'N'),
    },
    pile: s.pile.slice(),
    round: s.round,
    roundStarter: s.roundStarter,
    turn: s.turn,
    active: s.active,
    decider: s.decider,
    phase: s.phase,
    chain: s.chain,
    pendingInscription:
      veiled && s.pendingInscription && s.pawns[s.pendingInscription.pawnId]?.side === veiled
        ? { ...s.pendingInscription, inscription: hidden(s.pendingInscription.inscription) }
        : s.pendingInscription,
    pendingArrivals:
      veiled && s.pendingArrivals?.owner === veiled
        ? { ...s.pendingArrivals, epitaphs: s.pendingArrivals.epitaphs.map(hiddenEpitaph) }
        : s.pendingArrivals,
    pendingEpitaphChoice:
      veiled && s.pendingEpitaphChoice?.owner === veiled
        ? {
            ...s.pendingEpitaphChoice,
            candidates: s.pendingEpitaphChoice.candidates.map(hiddenEpitaph),
          }
        : s.pendingEpitaphChoice,
    rounds: s.rounds.slice(),
    result: s.result,
    ...(ledger ? { ledger } : {}),
  };
}

/**
 * Long Night (Veiled, issue #10): the side whose inscriptions are hidden from `viewer`, or null.
 * The Widow plays it against South: her pawns' inscriptions, and the Epitaphs made of them.
 */
export function veiledFrom(config: MatchConfig, viewer: Side): Side | null {
  const veiled = config.ledger?.houseRules.some(
    (r) => r.id === 'veiled' && (r.side ?? 'S') === viewer,
  );
  return veiled ? (viewer === 'S' ? 'N' : 'S') : null;
}

/** An inscription as the veiled side's opponent sees it: there, but blank. */
const hidden = (ins: Inscription): Inscription => ({
  links: [],
  recordedScore: 0,
  round: ins.round,
  veiled: true,
});
const hiddenEpitaph = (e: Epitaph): Epitaph => ({ ...e, inscription: hidden(e.inscription) });

/**
 * Hides the faces of cards drawn by the other side (§18.3), and under Veiled every route of the
 * veiled side: its inscriptions as they are set, kept or replaced, and the Epitaphs it takes or
 * discards. The moves themselves stay public. `config` is the match's (Veiled is a House Rule).
 */
export function redactEvents(
  events: readonly MatchEvent[],
  viewer: Side,
  config?: MatchConfig,
): MatchEvent[] {
  const veiled = config ? veiledFrom(config, viewer) : null;
  return events.map((e) => {
    if (e.type === 'CardDrawn' && e.side !== viewer) return { ...e, cardId: null, card: null };
    if (veiled === null) return e;
    if (e.type === 'InscriptionSet' && e.side === veiled)
      return { ...e, how: 'automatic', inscription: hidden(e.inscription) };
    if ((e.type === 'EpitaphReceived' || e.type === 'EpitaphDiscarded') && e.side === veiled)
      return { ...e, epitaph: hiddenEpitaph(e.epitaph) };
    return e;
  });
}
